import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Cpu, Eye, Move3d, Zap, HardDrive } from 'lucide-react';
import { ReadMore } from './ReadMore';
import { dataAdvantageData } from '../data/siteData';

const specIcons = {
  "Cameras": Camera, "Resolution": Eye, "Depth": Move3d,
  "Motion": Cpu, "Weight": Zap, "Storage": HardDrive,
};

const DataAdvantage = () => {
  const { sectionTag, title, subtitle, hardware } = dataAdvantageData;

  return (
    <section className="py-24 lg:py-32 border-t border-white/10" data-testid="data-advantage-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF7A00]/70 mb-4 block">{sectionTag}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl mb-4">{title}</h2>
          <p className="text-base text-white/40 max-w-2xl">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Hardware Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="lg:col-span-7 bg-[#141414] border border-white/10 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#FF7A00]" />
              <span className="font-mono text-xs tracking-[0.15em] uppercase text-white/40">Proprietary Hardware</span>
            </div>
            <h3 className="text-2xl lg:text-3xl font-extrabold mb-1">{hardware.name}</h3>
            <p className="text-sm text-white/40 mb-8">{hardware.tagline}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {hardware.specs.map((spec, i) => {
                const Icon = specIcons[spec.label] || Cpu;
                return (
                  <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <Icon size={16} className="text-[#FF7A00]/60 mb-2" />
                    <div className="font-mono text-sm font-semibold text-white">{spec.value}</div>
                    <div className="text-xs text-white/30 mt-0.5">{spec.label}</div>
                  </div>
                );
              })}
            </div>
            <ReadMore to="/technology/capture" testid="hardware-read-more" className="mt-8">Read more about the capture rig</ReadMore>
          </motion.div>

          {/* Capabilities */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5 bg-[#141414] border border-white/10 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#FF7A00]" />
              <span className="font-mono text-xs tracking-[0.15em] uppercase text-white/40">What the Data Enables</span>
            </div>
            <ul className="space-y-4">
              {hardware.capabilities.map((cap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FF7A00]/50 mt-2 flex-shrink-0" />
                  <span className="text-sm text-white/70 leading-relaxed">{cap}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DataAdvantage;
